(function () {
  'use strict';

  const routes = (window.Shopify && window.Shopify.routes) || { root: '/' };
  const moneyFormat = (window.Shopify && window.Shopify.currency && window.Shopify.currency.format) || '${{amount}}';

  function formatMoney(cents) {
    const value = (Number(cents) / 100).toFixed(2);
    return moneyFormat.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, value);
  }

  async function fetchCart() {
    const res = await fetch(routes.root + 'cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  async function addToCart(formData) {
    const res = await fetch(routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Add to cart failed');
    }
    return res.json();
  }

  async function changeCart(line, quantity) {
    const res = await fetch(routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    });
    if (!res.ok) throw new Error('Cart change failed');
    return res.json();
  }

  // ── Realtime chat (Firebase Firestore) ───────────────────────────────────
  // Lazily initialise a single Firebase connection shared by every chat module.
  // Falls back to local-only mode when no config is present or the SDK is absent.
  let firebaseChat; // undefined = untried, false = unavailable, object = ready
  function getFirebaseChat() {
    if (firebaseChat !== undefined) return firebaseChat;
    const cfg = (window.BLKSMIITH && window.BLKSMIITH.firebase) || null;
    if (!cfg || !cfg.apiKey || !cfg.projectId || !window.firebase) {
      firebaseChat = false;
      return firebaseChat;
    }
    try {
      const fb = window.firebase;
      const app = fb.apps && fb.apps.length ? fb.app() : fb.initializeApp(cfg);
      // App Check: attests requests originate from this site (reCAPTCHA v3), which
      // is the real defence against bots/scripts abusing the public Firebase keys.
      if (cfg.appCheckKey && fb.appCheck) {
        try {
          fb.appCheck(app).activate(cfg.appCheckKey, true);
        } catch (e) {
          console.error('[chat] App Check activation failed', e);
        }
      }
      const db = fb.firestore(app);
      const auth = fb.auth(app);
      const authReady = auth
        .signInAnonymously()
        .catch((e) => console.error('[chat] anonymous auth failed', e));
      const col = db.collection('chat_messages');
      firebaseChat = {
        enabled: true,
        // Subscribe to the newest 60 messages; returns an unsubscribe fn.
        onMessages(callback) {
          return col
            .orderBy('createdAt', 'desc')
            .limit(60)
            .onSnapshot(
              (snap) => {
                const msgs = [];
                snap.forEach((doc) => {
                  const d = doc.data();
                  const ts = d.createdAt && d.createdAt.toDate ? d.createdAt.toDate() : new Date();
                  msgs.push({
                    id: doc.id,
                    user: d.handle || 'GUEST',
                    text: d.text || '',
                    at: ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                  });
                });
                msgs.reverse(); // oldest → newest for display
                callback(msgs);
              },
              (err) => console.error('[chat] snapshot error', err)
            );
        },
        async send(handle, text) {
          await authReady;
          const stamp = fb.firestore.FieldValue.serverTimestamp();
          const uid = auth.currentUser && auth.currentUser.uid;
          // Write the message and the per-user rate-limit marker atomically so the
          // security rules can enforce a server-side cooldown that can't be bypassed.
          const batch = db.batch();
          batch.set(col.doc(), {
            handle: String(handle).slice(0, 40),
            text: String(text).slice(0, 140),
            createdAt: stamp,
          });
          if (uid) {
            batch.set(db.collection('rate_limits').doc(uid), { lastPost: stamp });
          }
          return batch.commit();
        },
      };
    } catch (e) {
      console.error('[chat] Firebase init failed', e);
      firebaseChat = false;
    }
    return firebaseChat;
  }

  // Persist an anonymous handle per browser so a visitor keeps the same name.
  function getChatHandle(config) {
    const base = (config && config.handle) || 'GUEST';
    const random = () => base + '_' + Math.floor(1000 + Math.random() * 9000);
    try {
      const KEY = 'blk_chat_handle';
      let handle = localStorage.getItem(KEY);
      if (!handle) {
        handle = random();
        localStorage.setItem(KEY, handle);
      }
      return handle;
    } catch (e) {
      return random();
    }
  }

  // Lightweight profanity mask. Not a security control (that's the rate limit +
  // Firestore rules) — just keeps the public board clean. Extend the list as needed.
  const BANNED_WORDS = [
    'fuck', 'shit', 'bitch', 'cunt', 'asshole', 'bastard', 'dick', 'piss',
    'nigger', 'nigga', 'faggot', 'fag', 'retard', 'slut', 'whore', 'rape',
  ];
  const BANNED_RE = new RegExp('\\b(' + BANNED_WORDS.join('|') + ')\\b', 'gi');
  function maskProfanity(text) {
    return text.replace(BANNED_RE, (word) => '*'.repeat(word.length));
  }

  // Detect URLs/links so they can be blocked. Catches protocols, www., bare
  // domains with a known TLD, and common "dot"/"[dot]" obfuscation.
  const URL_RE = new RegExp(
    '(https?:\\/\\/|www\\.|[a-z0-9-]+\\s*(?:\\.|\\[dot\\]|\\(dot\\)|\\sdot\\s)\\s*(?:com|net|org|io|co|gg|me|tv|xyz|shop|store|online|site|link|info|biz|app|dev|live|stream|club|ru|cn|uk|us|ca|de|fr|nl|eu|to|cc|ly|gl|be)\\b)',
    'i'
  );
  function containsUrl(text) {
    return URL_RE.test(text);
  }

  document.addEventListener('alpine:init', () => {
    const Alpine = window.Alpine;
    if (!Alpine) return;

    Alpine.store('cart', {
      open: false,
      loading: false,
      item_count: 0,
      items: [],
      total_price: 0,
      currency: 'USD',
      async load() {
        try {
          this.loading = true;
          const cart = await fetchCart();
          this.item_count = cart.item_count;
          this.items = cart.items;
          this.total_price = cart.total_price;
          this.currency = cart.currency;
        } finally {
          this.loading = false;
        }
      },
      async add(form) {
        const data = new FormData(form);
        await addToCart(data);
        await this.load();
        this.open = true;
      },
      async update(line, qty) {
        await changeCart(line, qty);
        await this.load();
      },
      money: formatMoney,
    });

    Alpine.store('ui', {
      mobile_menu: false,
      search_open: false,
      ticker_paused: false,
    });

    Alpine.data('messageBoard', (config) => ({
      input: '',
      max: (config && config.max) || 80,
      messages: [],
      handle: '',
      live: false,
      unsub: null,
      notice: '',
      cooldownMs: (config && config.cooldownMs) || 2000,
      lastSent: 0,
      init() {
        this.handle = getChatHandle(config);
        const chat = getFirebaseChat();
        if (chat && chat.enabled) {
          this.live = true;
          // Realtime: new messages from any visitor stream in here.
          this.unsub = chat.onMessages((msgs) => {
            this.messages = msgs.slice(-this.max);
            this.scrollToEnd();
          });
        }
      },
      destroy() {
        if (this.unsub) this.unsub();
      },
      flash(message) {
        this.notice = message;
        clearTimeout(this._noticeTimer);
        this._noticeTimer = setTimeout(() => {
          this.notice = '';
        }, 2500);
      },
      async send() {
        const now = Date.now();
        const remaining = this.cooldownMs - (now - this.lastSent);
        if (remaining > 0) {
          this.flash('Slow down — wait ' + Math.ceil(remaining / 1000) + 's.');
          return;
        }
        let text = this.input.trim();
        if (!text) return;
        if (containsUrl(text)) {
          this.flash('Links and URLs are not allowed.');
          return;
        }
        if (text.length > 140) text = text.slice(0, 140);
        text = maskProfanity(text);
        this.input = '';
        this.lastSent = now;
        const chat = getFirebaseChat();
        if (chat && chat.enabled) {
          try {
            // The realtime listener appends it once Firestore confirms the write.
            await chat.send(this.handle, text);
          } catch (e) {
            console.error('[chat] send failed', e);
            this.lastSent = 0; // let them retry after a genuine failure
            this.flash('Message blocked — try again shortly.');
          }
          return;
        }
        // Local-only fallback (Firebase not configured): visible to this tab only.
        this.messages.push({
          id: now,
          user: this.handle,
          text,
          at: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        });
        if (this.messages.length > this.max) this.messages.shift();
        this.scrollToEnd();
      },
      scrollToEnd() {
        this.$nextTick(() => {
          const scroller = this.$refs.scroller;
          if (scroller) scroller.scrollTop = scroller.scrollHeight;
        });
      },
    }));

    Alpine.data('quantity', (initial, min) => ({
      min: (min === undefined || min === null) ? 1 : min,
      value: (initial === undefined || initial === null) ? 1 : initial,
      inc() { this.value = this.value + 1; },
      dec() { this.value = Math.max(this.min, this.value - 1); },
    }));

    // Product variant selector: keeps the hidden variant id, add-to-cart button
    // and option availability in sync as the shopper picks options.
    Alpine.data('productForm', (config) => ({
      variants: (config && config.variants) || [],
      selected: (config && config.selected) || [],
      addLabel: (config && config.addLabel) || 'Add to cart',
      soldLabel: (config && config.soldLabel) || 'Sold out',
      // The variant matching every currently-selected option, or null.
      get currentVariant() {
        return (
          this.variants.find(
            (v) =>
              v.options.length === this.selected.length &&
              v.options.every((opt, i) => opt === this.selected[i])
          ) || null
        );
      },
      get available() {
        return !!(this.currentVariant && this.currentVariant.available);
      },
      get variantId() {
        return this.currentVariant ? this.currentVariant.id : '';
      },
      // True when some in-stock variant exists for this option value given the
      // shopper's other selections — used to grey out sold-out sizes.
      optionAvailable(optIndex, value) {
        return this.variants.some(
          (v) =>
            v.available &&
            v.options[optIndex] === value &&
            v.options.every((opt, i) => i === optIndex || this.selected[i] === opt)
        );
      },
    }));

    // Cursor-tracking CCTV camera: tilts toward the pointer (pseudo-3D),
    // sweeps on an idle patrol when the mouse is still, and respects
    // prefers-reduced-motion.
    Alpine.data('cctvCamera', () => ({
      mx: 0,
      my: 0,
      raf: null,
      idleTimer: null,
      init() {
        const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mq && mq.matches) return;
        this.svg = this.$el.querySelector('.cctv-camera__svg');
        this.onMove = (e) => {
          this.mx = e.clientX;
          this.my = e.clientY;
          if (!this.raf) this.raf = requestAnimationFrame(() => this.apply());
        };
        window.addEventListener('pointermove', this.onMove, { passive: true });
      },
      destroy() {
        if (this.onMove) window.removeEventListener('pointermove', this.onMove);
        if (this.raf) cancelAnimationFrame(this.raf);
        if (this.idleTimer) clearTimeout(this.idleTimer);
      },
      apply() {
        this.raf = null;
        const r = this.$el.getBoundingClientRect();
        if (!r.width) return;
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const nx = Math.max(-1, Math.min(1, (this.mx - cx) / (window.innerWidth * 0.5)));
        const ny = Math.max(-1, Math.min(1, (this.my - cy) / (window.innerHeight * 0.5)));
        this.$el.classList.add('is-tracking');
        if (this.svg) {
          this.svg.style.transform =
            'rotateX(' + (-ny * 20).toFixed(2) + 'deg) rotateY(' + (nx * 34).toFixed(2) +
            'deg) rotate(' + (nx * 5).toFixed(2) + 'deg)';
        }
        this.$el.style.setProperty('--cctv-glint-x', (nx * 4).toFixed(2) + 'px');
        this.$el.style.setProperty('--cctv-glint-y', (ny * 3).toFixed(2) + 'px');
        if (this.idleTimer) clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => this.rest(), 2600);
      },
      rest() {
        this.$el.classList.remove('is-tracking');
        if (this.svg) this.svg.style.transform = '';
        this.$el.style.removeProperty('--cctv-glint-x');
        this.$el.style.removeProperty('--cctv-glint-y');
      },
    }));

    Alpine.data('xpFeaturedPopup', (config) => ({
      open: false,
      x: 0,
      y: 0,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      originX: 0,
      originY: 0,
      get positionStyle() {
        return `left: calc(50% + ${this.x}px); top: calc(50% + ${this.y}px); transform: translate(-50%, -50%);`;
      },
      init() {
        const key = (config && config.storageKey) || 'xp-featured-popup';
        const showOnce = config && config.showOnce !== false;
        if (showOnce && localStorage.getItem(key) === 'dismissed') return;

        const delay = (config && config.delay) || 0;
        const show = () => { this.open = true; };
        if (delay > 0) setTimeout(show, delay);
        else this.$nextTick(show);
      },
      close() {
        this.open = false;
        const key = (config && config.storageKey) || 'xp-featured-popup';
        if (config && config.showOnce !== false) {
          localStorage.setItem(key, 'dismissed');
        }
      },
      startDrag(event) {
        if (event.button !== 0) return;
        this.dragging = true;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.originX = this.x;
        this.originY = this.y;

        const onMove = (e) => {
          if (!this.dragging) return;
          this.x = this.originX + (e.clientX - this.dragStartX);
          this.y = this.originY + (e.clientY - this.dragStartY);
        };
        const onUp = () => {
          this.dragging = false;
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      },
    }));
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (window.Alpine && window.Alpine.store && window.Alpine.store('cart')) {
      window.Alpine.store('cart').load();
    }
  });
})();
